import { CardBlock, InputBlock, parseTokens } from '@coast/schemas';
import { JSONSchema7 } from 'json-schema';
import { isEmpty, isNil, mapValues, omitBy, sortBy } from 'lodash';
import moment from 'moment';

import { getStore } from '@shared/store';
import { API } from '@shared/types/api';
import { omitTypeName } from '@shared/util/omitTypeName';
import { isNoCodeCard } from '@shared/util/workflow';
import { CommonFields, CustomFields, Fields } from '../types';

export { getTextPropsFromTextStyle, resolveTextColorProps } from './getTextPropsFromTextStyle';
export type { TextStyleProps, ThemeColors } from './getTextPropsFromTextStyle';

export function extractCardDefinitionDefaultValues(definition: JSONSchema7): CustomFields {
  const store = getStore();
  return mapValues(definition?.properties, fieldDefinition => {
    const value = (fieldDefinition as JSONSchema7)?.default;
    if (!value) {
      return undefined;
    }
    return parseTokens(value, {
      resolveCurrentUserId: () => store.getState().appState.currentUserId,
    });
  });
}

export function extractAllFields(
  card: API.CardFragment,
  definition: JSONSchema7,
  extractDefinitionDefaults: boolean = true,
): CommonFields & Fields {
  const isNoCode = isNoCodeCard(card);
  const reminders = card.reminders?.map(omitTypeName) ?? [];

  return {
    ...(extractDefinitionDefaults ? extractCardDefinitionDefaultValues(definition) : undefined),
    ...(isNoCode ? undefined : card.fields),
    // @ts-expect-error -- strict migration
    startDate: card.startDate,
    // @ts-expect-error -- strict migration
    endDate: card.endDate,
    kanbanSortKey: card.kanbanSortKey,
    // @ts-expect-error -- strict migration
    sequenceNumber: card.sequenceNumber,
    urgent: card.urgent,
    threadId: (card as API.CardFragment).threadId,
    createdAt: card.createdAt,
    updatedAt: card.updatedAt,
    assignees: card.assignees?.map(u => u.id),
    batchByAssignee: false,
    // @ts-expect-error -- strict migration
    batchByAssigneeName: undefined,
    // @ts-expect-error -- strict migration
    recurrence: card.recurrence?.rule,
    // @ts-expect-error -- strict migration
    reminder: sortBy(reminders, reminder => moment.duration(reminder.duration)),
    ...(isNoCode ? card.fields : undefined),
  };
}

export function omitCommonFields(
  fields: Fields,
  cardDefinition: API.CardDefinitionFragment,
  isNoCode?: boolean,
): CustomFields {
  const {
    endDate,
    kanbanSortKey,
    urgent,
    comments,
    thread,
    threadId,
    updatedAt,
    recurrence,
    recurrenceUpdateScope,
    reminder,
    batchByAssignee,
    batchByAssigneeName,
    cardDefinitionId,
    workflowTemplateId,
    createdAt,
    sequenceNumber,
    startDate,
    assignees,
    ...customFields
  } = fields;
  const omitIfEmpty = ['assignees', 'startDate'];
  const validComponentIds = Object.keys(cardDefinition.definition.properties);
  Object.keys(customFields).forEach(id => {
    const isOmitIfEmptyAndIsEmpty = omitIfEmpty.includes(id) && isEmpty(customFields[id]);
    const isNotInWorkflow = !validComponentIds.includes(id) && id !== 'name';

    if (isNotInWorkflow || isOmitIfEmptyAndIsEmpty) {
      delete customFields[id];
    }
  });
  if (isNoCode) {
    if (!isNil(startDate)) {
      customFields['startDate'] = startDate;
    }
    if (!isEmpty(assignees)) {
      customFields['assignees'] = assignees;
    }
  }
  return omitBy(customFields, isNil);
}

export function extractBlockDefaultValues(blocks: CardBlock[]): Partial<CommonFields & Fields> {
  const store = getStore();
  return blocks
    .filter((block): block is InputBlock => block.type === 'input')
    .filter(block => !!block.defaultValue)
    .reduce(
      (values, block) => ({
        ...values,
        [block.name]: parseTokens(block.defaultValue, {
          resolveCurrentUserId: () => store.getState().appState.currentUserId,
        }),
      }),
      {},
    );
}

export function extractFieldOverrides(fieldOverrides: Fields | undefined): Record<string, unknown> {
  const store = getStore();
  const currUserId = store.getState().appState.currentUserId;
  return mapValues(fieldOverrides, value =>
    parseTokens(value, { resolveCurrentUserId: () => currUserId }),
  );
}

export function getBlockWidths(blocks: CardBlock[]): string[] {
  let numUnset = 0;
  let total = 0;
  let widths: string[] = [];
  blocks.forEach((block, i) => {
    if ((block as any)?.width) {
      widths[i] = (block as any).width;
      total += parseInt(widths[i]);
    } else {
      // @ts-expect-error -- strict migration
      widths[i] = undefined;
      numUnset++;
    }
  });
  const remainder = (100 - total) / numUnset;
  for (let i = 0; i < widths.length; i++) {
    if (widths[i] === undefined) {
      widths[i] = `${remainder}%`;
    }
  }
  return widths;
}
